// A one-shot, in-memory hand-off between two admin screens.
//
// Accept-invite ends by sending the user to the sign-in screen, which must say
// WHY they are there ("password set, now sign in"). The three obvious carriers
// are all wrong here: a query string would put the state in the address bar
// where a bookmark or a reload replays it forever; `sessionStorage` would keep
// it across a browser restart for the same reason; and passing it through the
// router's history state needs a module augmentation for one boolean.
//
// So it is a module-level cell, SET by the screen that navigates and TAKEN
// (cleared) by the screen that renders it. A reload loses it, which is correct:
// the message is a consequence of the navigation that just happened, not a
// property of the page. Nothing sensitive is ever put in here — it carries a
// closed union of message kinds, never a token, password or code.
//
//   password_set    — accept-invite finished; sign in to continue.
//   session_expired — a signed-in screen's write came back 401 WITHOUT a TOTP
//                     verdict (classifyTotpCheckFailure): the session died
//                     mid-action, so the screen sent the user here. The banner
//                     must say it was the SESSION that failed — the 2026-08-17
//                     probe found both TOTP-checking screens blaming the
//                     operator's code for it instead.

//   password_reset  — the reset screen set a new password (D-587); sign in
//                     with it and the two-factor code.

export type AdminFlash = "password_set" | "session_expired" | "password_reset";

let pending: AdminFlash | undefined;

export function setAdminFlash(flash: AdminFlash): void {
  pending = flash;
}

/** Read and clear. Returns undefined when there is nothing to say. */
export function takeAdminFlash(): AdminFlash | undefined {
  const value = pending;
  pending = undefined;
  return value;
}

/** Test hook: drop anything queued so specs never leak into each other. */
export function clearAdminFlash(): void {
  pending = undefined;
}
